import { camelCase } from 'lodash'
import * as Yup from 'yup'
import { Alert, Button, Col, Form, ListGroup, Modal, InputGroup, Row } from 'react-bootstrap'
import type { AlertState } from '../../common'

type RowValues = {
  id: number
  name: string
  characterStyleSettingType: string
  description: string
}

type UpsertValues = {
  characterStyleSettingType: string
  description: string
  name: string
}

type CharacterStyleSetting = {
  id: number
  characterStyleSettingType: string
  name: string
}

type EnumValues = {
  characterStyleSettingTypes: Record<string, string>
}

const _CharacterStyleSettingForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleSubmit,
  enumValues,
  isSettingWritable,
  selectedIds,
  dispatch,
  selectId,
  unselectId,
  rowIndex
}: {
  values: RowValues | null
  errors: Partial<Record<keyof RowValues, string>>
  touched: Partial<Record<keyof RowValues, boolean>>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleSubmit: (values: RowValues) => void
  enumValues: EnumValues
  isSettingWritable: boolean
  selectedIds: number[]
  dispatch: (action: unknown) => void
  selectId: (id: number) => unknown
  unselectId: (id: number) => unknown
  rowIndex: number
}) => {
  if (!values) {
    return <></>
  }
  const typeString = enumValues.characterStyleSettingTypes[camelCase(values?.characterStyleSettingType)]
  const combinedColumnString = `${values.description}(${typeString})`
  return (
    <Row sm='auto' className={`row-${rowIndex + 1}`}>
      <Col sm={2}>{combinedColumnString}</Col>
      <Col>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id={`nameInput-${values.id}`}
            type='text'
            name='name'
            value={values.name || ''}
            disabled={!isSettingWritable}
            onChange={handleChange}
            onBlur={(_) => handleSubmit(values)}
            isInvalid={!!(touched.name && errors.name)}
          />
          <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
        </InputGroup>
      </Col>
      <Col sm='auto'>
        <Form.Check.Input
          type='checkbox'
          name='checkedGroups'
          value={values.id}
          checked={selectedIds?.includes(values.id)}
          onChange={(e) => dispatch((e.target.checked ? selectId : unselectId)(parseInt(e.target.value)))}
        />
      </Col>
    </Row>
  )
}

export const CharacterStyleSettingForm = Object.assign(_CharacterStyleSettingForm, {
  schema: Yup.object().shape({
    name: Yup.string().trim().required()
  })
})

export const CharacterStyleSettingDeleteForm = ({
  handleSubmit,
  title,
  submitButtonText,
  onClickCancel,
  selectedIds,
  characterStyleSettings,
  enumValues
}: {
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  title: string
  submitButtonText: string
  onClickCancel: () => void
  selectedIds: number[]
  characterStyleSettings: CharacterStyleSetting[] | undefined
  enumValues: EnumValues
}) => {
  const selectedIdSet = new Set(selectedIds)
  const deleteStyles = characterStyleSettings?.filter((s) => selectedIdSet.has(s.id))
  return (
    <>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          <p>以下の文字スタイルを削除します。よろしいですか？</p>
          <ListGroup>
            {deleteStyles?.map((style) => (
              <ListGroup.Item key={style.id}>
                id: {style.id}, 種別:{' '}
                {enumValues.characterStyleSettingTypes[camelCase(style.characterStyleSettingType)]}, 名称: {style.name}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit'>{submitButtonText}</Button>
        </Modal.Footer>
      </Form>
    </>
  )
}

const _CharacterStyleSettingUpsertForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isValid,
  fetching,
  isSettingWritable,
  title,
  onClickCancel,
  submitButtonText,
  enumValues,
  alert
}: {
  values: UpsertValues
  errors: Partial<Record<keyof UpsertValues, string>>
  touched: Partial<Record<keyof UpsertValues, boolean>>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  isValid: boolean
  fetching: boolean
  isSettingWritable: boolean
  title: string
  onClickCancel: () => void
  submitButtonText: string
  enumValues: EnumValues
  alert: AlertState | null
}) => (
  <>
    <Modal.Header>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    {alert?.show && alert?.alertBody && (
      <Alert className='upsert-char-style-setting-errors-alert' variant='danger'>
        {alert.alertBody}
      </Alert>
    )}
    <Form onSubmit={handleSubmit}>
      <Modal.Body>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column className='col-3'>
            文字スタイル種別
          </Form.Label>
          <Col>
            <Form.Control
              as='select'
              type='text'
              name='characterStyleSettingType'
              value={values.characterStyleSettingType || ''}
              onBlur={handleBlur}
              onChange={handleChange}
              isInvalid={!!(touched.characterStyleSettingType && errors.characterStyleSettingType)}
              disabled={!isSettingWritable}
            >
              {Object.keys(enumValues.characterStyleSettingTypes).map((k) => (
                <option key={k} value={k}>
                  {enumValues.characterStyleSettingTypes[k]}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type='invalid'>{errors.characterStyleSettingType}</Form.Control.Feedback>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column className='col-3'>
            文字スタイルの説明
          </Form.Label>
          <Col>
            <Form.Control
              type='text'
              name='description'
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!(touched.description && errors.description)}
            />
            <Form.Control.Feedback type='invalid'>{errors.description}</Form.Control.Feedback>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column className='col-3'>
            文字スタイルの名称
          </Form.Label>
          <Col>
            <Form.Control
              type='text'
              name='name'
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!(touched.name && errors.name)}
            />
            <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
          </Col>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClickCancel}>
          キャンセル
        </Button>
        <Button type='submit' disabled={!isValid || fetching}>
          {submitButtonText}
        </Button>
      </Modal.Footer>
    </Form>
  </>
)

export const CharacterStyleSettingUpsertForm = Object.assign(_CharacterStyleSettingUpsertForm, {
  schema: Yup.object().shape({
    characterStyleSettingType: Yup.string().trim().required(),
    description: Yup.string().trim().required(),
    name: Yup.string().trim().required()
  })
})
